/**
 * HostPoint POS - Offline Storage Service
 * Uses IndexedDB for persistent local storage with sync capabilities
 */

const DB_NAME = 'hostpoint-pos-db';
const DB_VERSION = 1;

// Store names
const STORES = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  PENDING_ORDERS: 'pending-orders',
  PENDING_PAYMENTS: 'pending-payments',
  CACHED_DATA: 'cached-data',
  SYNC_QUEUE: 'sync-queue',
  USER_SESSION: 'user-session'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      reject(request.error);
    };
    
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Products store
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productsStore = database.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
        productsStore.createIndex('category', 'category', { unique: false });
        productsStore.createIndex('property_id', 'property_id', { unique: false });
      }
      
      // Categories store
      if (!database.objectStoreNames.contains(STORES.CATEGORIES)) {
        const categoriesStore = database.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
        categoriesStore.createIndex('property_id', 'property_id', { unique: false });
      }
      
      // Pending orders store (for offline orders)
      if (!database.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const ordersStore = database.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'id' });
        ordersStore.createIndex('created_at', 'created_at', { unique: false });
        ordersStore.createIndex('synced', 'synced', { unique: false });
      }
      
      // Pending payments store
      if (!database.objectStoreNames.contains(STORES.PENDING_PAYMENTS)) {
        const paymentsStore = database.createObjectStore(STORES.PENDING_PAYMENTS, { keyPath: 'id' });
        paymentsStore.createIndex('order_id', 'order_id', { unique: false });
      }
      
      // General cached data store
      if (!database.objectStoreNames.contains(STORES.CACHED_DATA)) {
        database.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
      }
      
      // Sync queue for background sync
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('status', 'status', { unique: false });
      }
      
      // User session store
      if (!database.objectStoreNames.contains(STORES.USER_SESSION)) {
        database.createObjectStore(STORES.USER_SESSION, { keyPath: 'key' });
      }
      
    };
  });
}

/**
 * Generic store operations
 */
async function getStore(storeName, mode = 'readonly') {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
}

/**
 * Save item to store
 */
export async function saveItem(storeName, item) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get item by key
 */
export async function getItem(storeName, key) {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all items from store
 */
export async function getAllItems(storeName) {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete item by key
 */
export async function deleteItem(storeName, key) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items in store
 */
export async function clearStore(storeName) {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index
 */
export async function getByIndex(storeName, indexName, value) {
  const store = await getStore(storeName);
  const index = store.index(indexName);
  return new Promise((resolve, reject) => {
    const request = index.getAll(value);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ============== PRODUCT OPERATIONS ==============

/**
 * Cache products for offline use
 */
export async function cacheProducts(products) {
  const store = await getStore(STORES.PRODUCTS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    let completed = 0;
    const total = products.length;
    
    if (total === 0) {
      resolve();
      return;
    }
    
    products.forEach(product => {
      const request = store.put({
        ...product,
        cached_at: new Date().toISOString()
      });
      
      request.onsuccess = () => {
        completed++;
        if (completed === total) resolve();
      };
      
      request.onerror = () => reject(request.error);
    });
  });
}

/**
 * Get cached products
 */
export async function getCachedProducts(propertyId) {
  if (propertyId) {
    return getByIndex(STORES.PRODUCTS, 'property_id', propertyId);
  }
  return getAllItems(STORES.PRODUCTS);
}

/**
 * Get products by category
 */
export async function getProductsByCategory(category) {
  return getByIndex(STORES.PRODUCTS, 'category', category);
}

// ============== CATEGORY OPERATIONS ==============

/**
 * Cache categories for offline use
 */
export async function cacheCategories(categories) {
  const store = await getStore(STORES.CATEGORIES, 'readwrite');
  
  return new Promise((resolve, reject) => {
    let completed = 0;
    const total = categories.length;
    
    if (total === 0) {
      resolve();
      return;
    }
    
    categories.forEach(category => {
      const request = store.put({
        ...category,
        cached_at: new Date().toISOString()
      });
      
      request.onsuccess = () => {
        completed++;
        if (completed === total) resolve();
      };
      
      request.onerror = () => reject(request.error);
    });
  });
}

/**
 * Get cached categories
 */
export async function getCachedCategories(propertyId) {
  if (propertyId) {
    return getByIndex(STORES.CATEGORIES, 'property_id', propertyId);
  }
  return getAllItems(STORES.CATEGORIES);
}

// ============== OFFLINE ORDER OPERATIONS ==============

/**
 * Save order for offline sync
 */
export async function saveOfflineOrder(order) {
  const offlineOrder = {
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    ...order,
    is_offline: true,
    synced: false,
    created_at: new Date().toISOString(),
    token: localStorage.getItem('auth_token')
  };
  
  await saveItem(STORES.PENDING_ORDERS, offlineOrder);
  
  // Add to sync queue
  await addToSyncQueue('order', offlineOrder.id, order);
  
  return offlineOrder;
}

/**
 * Get pending offline orders
 */
export async function getPendingOrders() {
  const orders = await getAllItems(STORES.PENDING_ORDERS);
  return orders.filter(o => !o.synced);
}

/**
 * Mark order as synced
 */
export async function markOrderSynced(offlineId, serverId) {
  const order = await getItem(STORES.PENDING_ORDERS, offlineId);
  if (order) {
    order.synced = true;
    order.server_id = serverId;
    order.synced_at = new Date().toISOString();
    await saveItem(STORES.PENDING_ORDERS, order);
  }
}

// ============== SYNC QUEUE OPERATIONS ==============

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(type, referenceId, data) {
  const queueItem = {
    type,
    reference_id: referenceId,
    data,
    status: 'pending',
    created_at: new Date().toISOString(),
    retry_count: 0
  };
  
  await saveItem(STORES.SYNC_QUEUE, queueItem);
  
  // Trigger background sync if available
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register(`sync-${type}s`);
  }
  
  return queueItem;
}

/**
 * Get pending sync items
 */
export async function getPendingSyncItems(type) {
  const items = await getByIndex(STORES.SYNC_QUEUE, 'type', type);
  return items.filter(item => item.status === 'pending');
}

/**
 * Update sync item status
 */
export async function updateSyncItemStatus(id, status, error = null) {
  const item = await getItem(STORES.SYNC_QUEUE, id);
  if (item) {
    item.status = status;
    item.updated_at = new Date().toISOString();
    if (error) {
      item.error = error;
      item.retry_count = (item.retry_count || 0) + 1;
    }
    await saveItem(STORES.SYNC_QUEUE, item);
  }
}

// ============== CACHED DATA OPERATIONS ==============

/**
 * Cache generic data with expiry
 */
export async function cacheData(key, data, expiryMinutes = 60) {
  const cacheItem = {
    key,
    data,
    cached_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + expiryMinutes * 60 * 1000).toISOString()
  };
  
  await saveItem(STORES.CACHED_DATA, cacheItem);
}

/**
 * Get cached data (returns null if expired)
 */
export async function getCachedData(key) {
  const item = await getItem(STORES.CACHED_DATA, key);
  
  if (!item) return null;
  
  // Check if expired
  if (new Date(item.expires_at) < new Date()) {
    await deleteItem(STORES.CACHED_DATA, key);
    return null;
  }
  
  return item.data;
}

// ============== USER SESSION ==============

/**
 * Save user session for offline access
 */
export async function saveUserSession(user, outlet, property) {
  await saveItem(STORES.USER_SESSION, { key: 'current_user', data: user });
  await saveItem(STORES.USER_SESSION, { key: 'current_outlet', data: outlet });
  await saveItem(STORES.USER_SESSION, { key: 'current_property', data: property });
}

/**
 * Get user session
 */
export async function getUserSession() {
  const user = await getItem(STORES.USER_SESSION, 'current_user');
  const outlet = await getItem(STORES.USER_SESSION, 'current_outlet');
  const property = await getItem(STORES.USER_SESSION, 'current_property');
  
  return {
    user: user?.data,
    outlet: outlet?.data,
    property: property?.data
  };
}

/**
 * Clear user session
 */
export async function clearUserSession() {
  await clearStore(STORES.USER_SESSION);
}

// ============== SYNC STATUS ==============

/**
 * Get sync status summary
 */
export async function getSyncStatus() {
  const pendingOrders = await getPendingOrders();
  const pendingPayments = await getAllItems(STORES.PENDING_PAYMENTS);
  const syncQueue = await getAllItems(STORES.SYNC_QUEUE);
  
  return {
    pendingOrders: pendingOrders.length,
    pendingPayments: pendingPayments.filter(p => !p.synced).length,
    queuedItems: syncQueue.filter(i => i.status === 'pending').length,
    failedItems: syncQueue.filter(i => i.status === 'failed').length,
    lastSyncAttempt: localStorage.getItem('last_sync_attempt'),
    isOnline: navigator.onLine
  };
}

/**
 * Attempt to sync all pending items
 */
export async function syncAll(apiUrl, token) {
  const results = {
    orders: { synced: 0, failed: 0 },
    payments: { synced: 0, failed: 0 }
  };
  
  if (!navigator.onLine) {
    return results;
  }
  
  localStorage.setItem('last_sync_attempt', new Date().toISOString());
  
  // Sync pending orders
  const pendingOrders = await getPendingOrders();
  for (const order of pendingOrders) {
    try {
      const response = await fetch(`${apiUrl}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || order.token}`
        },
        body: JSON.stringify(order.data || order)
      });
      
      if (response.ok) {
        const serverOrder = await response.json();
        await markOrderSynced(order.id, serverOrder.id);
        results.orders.synced++;
      } else {
        results.orders.failed++;
      }
    } catch (error) {
      results.orders.failed++;
    }
  }
  
  return results;
}

// Initialize DB on module load
initDB().catch(console.error);

export default {
  initDB,
  saveItem,
  getItem,
  getAllItems,
  deleteItem,
  clearStore,
  cacheProducts,
  getCachedProducts,
  cacheCategories,
  getCachedCategories,
  saveOfflineOrder,
  getPendingOrders,
  markOrderSynced,
  cacheData,
  getCachedData,
  saveUserSession,
  getUserSession,
  clearUserSession,
  getSyncStatus,
  syncAll,
  STORES
};
